/**
 * Physics-implementation-agnostic collision query interface.
 *
 * The player motor only talks to this interface. The Rapier-backed
 * implementation lives in PhysicsWorld.ts; a different backend can be dropped
 * in by implementing `CollisionWorld`.
 */
import { Vec3 } from '../core/Vec3';
import type { SurfaceMaterialDef } from '../world/SurfaceMaterials';

export interface CapsuleShape {
  radius: number;
  /** Half length of the inner segment (total height = 2 * (halfSegment + radius)). */
  halfSegment: number;
}

export class SweepHit {
  /** Fraction of the requested displacement travelled before contact, in [0, 1]. */
  fraction = 1;
  /** World-space surface normal, pointing out of the hit surface toward the player. */
  readonly normal = new Vec3();
  readonly point = new Vec3();
  colliderId = -1;
  material: SurfaceMaterialDef | null = null;
}

export class ProximityContact {
  /** Signed distance between capsule and surface; negative means penetration. */
  distance = 0;
  /** Normal pointing out of the surface toward the player. */
  readonly normal = new Vec3();
  /** Closest point on the surface. */
  readonly point = new Vec3();
  /** True when the closest point lies on a face; false at an edge or vertex, where the normal is a blend. */
  onFace = true;
  colliderId = -1;
  material: SurfaceMaterialDef | null = null;
}

export interface QueryStats {
  queries: number;
  queryMs: number;
}

export interface CollisionWorld {
  /**
   * Sweeps the capsule whose segment centre is `center` (oriented along `up`)
   * by `delta`. Returns true and fills `out` on the first solid hit, where
   * `fraction` is where the gap to the surface reaches `targetDistance`
   * (the collision skin).
   */
  sweepCapsule(center: Readonly<Vec3>, up: Readonly<Vec3>, delta: Readonly<Vec3>, shape: CapsuleShape, targetDistance: number, out: SweepHit): boolean;
  /**
   * Collects solids within `maxDistance` of the capsule (including
   * penetrations) into `out`, starting at index 0. Returns the count written.
   * `out` grows if needed; entries are reused between calls.
   */
  contacts(center: Readonly<Vec3>, up: Readonly<Vec3>, shape: CapsuleShape, maxDistance: number, out: ProximityContact[]): number;
  readonly colliderCount: number;
  readonly stats: QueryStats;
  resetStats(): void;
}
